import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Modal,
  Input,
  Form,
  Row,
  Col,
  Typography,
  notification,
  Button,
} from "antd";
import styles from "pages/accountant/finance/finance.module.scss";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import {
  MasterInvoice,
  MasterAccrual,
  MasterListItem,
} from "pages/accountant/finance/types";
import useMasterInvoiceStore from "stores/accountant/masterInvoice";
import clsx from "clsx";

type ComponentProps = {};

const { Title } = Typography;

const Page: React.FC<ComponentProps> = () => {
  const {
    list,
    setList,
    isChangeStatusInProcess,
    setIsChangeStatusInProcess,
    isPaymentInProcess,
    setIsPaymentInProcess,
    isTableHidden,
    setIsTableHidden,
  } = useMasterInvoiceStore((store) => store);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Get all knowledge base items
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/master_invoices`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) {
            const errorData = await response.json();

            const errorText: { [key in typeof errorCode]: string } = {
              amountIsEmpty: "Сумма не может быть пустой",
              userFileIdNotFound: "Файл не найден в базе данных",
            };

            const errorFields: string[] = errorData.fields;
            const errorCode: "amountIsEmpty" | "userFileIdNotFound" =
              errorData.errorCode;

            if (errorFields.length) {
              const errorMessage = errorText[errorCode];

              errorFields.forEach((errorField) => {
                form.setFields([
                  {
                    name: errorField,
                    errors: [errorMessage],
                  },
                ]);
              });
            }

            throw new Error();
          }

          const data = await response.json();

          if (!data?.records) throw new Error();

          const invoices: MasterInvoice[] = data.records.map(
            (invoice: MasterInvoice) => ({
              ...invoice,
              type: "invoice",
            })
          );

          const accruals: MasterAccrual[] = data.accruals.map(
            (accrual: MasterAccrual) => ({
              ...accrual,
              type: "accrual",
            })
          );

          const records = [...invoices, ...accruals]
            .sort((a, b) => {
              const dateA =
                a.type === "invoice"
                  ? new Date(a.created_at).getTime()
                  : new Date(a.date).getTime();
              const dateB =
                b.type === "invoice"
                  ? new Date(b.created_at).getTime()
                  : new Date(b.date).getTime();
              return dateA - dateB;
            })
            .filter((item) => item.type === "invoice");

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list, form]
  );

  //  When started, get all data
  useEffect(() => {
    getList();
  }, [getList]);

  const handleCancelButton = useCallback(
    (masterInvoice?: MasterInvoice) => {
      if (!masterInvoice?.id) return;

      let cancelComment = "";

      Modal.confirm({
        title: "Вы уверены, что хотите отменить этот счет?",
        content: (
          <div className={styles.modalContent}>
            <div className={styles.warningText}>
              Это действие нельзя будет отменить.
            </div>
            <Input.TextArea
              className={styles.commentInput}
              placeholder="Введите комментарий"
              onChange={(e) => (cancelComment = e.target.value)}
            />
          </div>
        ),
        okText: "Да, отменить",
        cancelText: "Отмена",
        onOk: async () => {
          if (!cancelComment.trim()) {
            Modal.error({
              title: "Ошибка",
              content: "Введите комментарий перед отменой.",
            });
            return Promise.reject();
          }

          setIsChangeStatusInProcess((prev) => ({
            ...prev,
            [masterInvoice.id]: true,
          }));

          const fetchData = async () => {
            const url = `${apiDomain}/api/records/master_invoices/${masterInvoice.id}`;
            const options: RequestInit = {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                status: "canceled",
                cancel_comment: cancelComment,
              }),
              credentials: "include",
            };

            try {
              const response = await fetch(url, options);
              if (!response.ok) throw new Error();

              await getList(true);
            } catch (error) {
              openNotificationWithError({
                message: "Ошибка при изменении статуса",
              });
            } finally {
              setIsChangeStatusInProcess((prev) => ({
                ...prev,
                [masterInvoice.id]: false,
              }));
            }
          };

          return fetchData();
        },
      });
    },
    [getList, openNotificationWithError, setIsChangeStatusInProcess]
  );

  const handleShowTable = useCallback(() => {
    setIsTableHidden((isTableHidden) => {
      return !isTableHidden;
    });
  }, [setIsTableHidden]);

  const handlePaymentButton = useCallback(
    (masterInvoice?: MasterInvoice) => {
      if (!masterInvoice?.id) return;

      setIsPaymentInProcess((prev) => ({
        ...prev,
        [masterInvoice.id]: true,
      }));

      const fetchData = async () => {
        const url = `${apiDomain}/api/records/master_invoices/${masterInvoice.id}`;
        const options: RequestInit = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "paid",
          }),
          credentials: "include",
        };

        try {
          const response = await fetch(url, options);
          if (!response.ok) throw new Error();

          await getList(true);
        } catch (error) {
          openNotificationWithError({
            message: "Ошибка при изменении статуса",
          });
        } finally {
          setIsPaymentInProcess((prev) => ({
            ...prev,
            [masterInvoice.id]: false,
          }));
        }
      };

      fetchData();
    },
    [getList, openNotificationWithError, setIsPaymentInProcess]
  );

  //  Amount changed
  useEffect(() => {
    const allValues = form.getFieldsValue();

    const amount = !allValues?.amount
      ? ""
      : (allValues?.amount as string)
          .replace(/[^\d.,]/g, "")
          .replace(/,/g, ".")
          .replace(/^(\d*(\.\d{0,2})?).*$/, "$1");

    form.setFieldsValue({
      amount: amount,
    });
  }, [form, values?.amount]);

  //  Set columns for table
  const columnsNotPaid: ColumnsType = useMemo(() => {
    const statusMap: Record<string, string> = {
      unconfirmed: "Добавлен",
      confirmed: "Подтвержден",
      canceled: "Отменен",
      paid: "Оплачен",
    };

    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          return record.id;
        },
      },
      {
        title: "Дата",
        key: "created_at",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedDate = record.created_at
              ? new Date(record.created_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          const formattedDate = record.date
            ? new Date(record.date).toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : null;

          return formattedDate;
        },
      },
      {
        title: "Мастер",
        key: "master",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return record.master?.name;
          }

          return null;
        },
      },
      {
        title: "Сумма",
        key: "paid",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedAmount = parseFloat(record.amount)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

            return formattedAmount;
          }

          return null;
        },
      },
      {
        title: "Файл",
        key: "file",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record?.user_file?.file_path) return null;

            return (
              <a
                href={`/userFiles/${record.user_file.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.user_file.file_name}
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Ссылка",
        key: "link",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.invoice_link) return null;

            return (
              <a
                href={record.invoice_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ссылка
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.comment) return null;

            return record.comment;
          }

          return null;
        },
      },
      {
        title: "Статус",
        key: "status",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return statusMap[record.status] ?? null;
          }

          return null;
        },
      },
      {
        key: "confirm",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;
          const recordStyles = record.styles;

          if (record.type === "invoice") {
            if (record.status !== "confirmed") return null;

            return (
              <Button
                onClick={() => handlePaymentButton(record)}
                className={recordStyles?.actionButton}
                loading={!!isPaymentInProcess?.[record?.id]}
              >
                Оплатить
              </Button>
            );
          }

          return null;
        },
      },
      {
        key: "cancel",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;
          const recordStyles = record.styles;

          if (record.type === "invoice") {
            if (record.status !== "unconfirmed") return null;

            return (
              <Button
                onClick={() => handleCancelButton(record)}
                className={recordStyles?.actionButton}
                loading={!!isChangeStatusInProcess?.[record?.id]}
              >
                Отменить
              </Button>
            );
          }

          return null;
        },
      },
    ];

    return result;
  }, [
    isChangeStatusInProcess,
    isPaymentInProcess,
    handleCancelButton,
    handlePaymentButton,
  ]);

  //  Set columns for table
  const columnsAllButNotPaid: ColumnsType = useMemo(() => {
    const statusMap: Record<string, string> = {
      unconfirmed: "Добавлен",
      confirmed: "Подтвержден",
      canceled: "Отменен",
      paid: "Оплачен",
    };

    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          return record.id;
        },
      },
      {
        title: "Дата",
        key: "created_at",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedDate = record.created_at
              ? new Date(record.created_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          const formattedDate = record.date
            ? new Date(record.date).toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : null;

          return formattedDate;
        },
      },
      {
        title: "Мастер",
        key: "master",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return record.master?.name;
          }

          return null;
        },
      },
      {
        title: "Сумма",
        key: "paid",
        align: "right",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            const formattedAmount = parseFloat(record.amount)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

            return formattedAmount;
          }

          return null;
        },
      },
      {
        title: "Файл",
        key: "file",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record?.user_file?.file_path) return null;

            return (
              <a
                href={`/userFiles/${record.user_file.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.user_file.file_name}
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Ссылка",
        key: "link",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.invoice_link) return null;

            return (
              <a
                href={record.invoice_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ссылка
              </a>
            );
          }

          return null;
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!record.comment) return null;

            return record.comment;
          }

          return null;
        },
      },
      {
        title: "Статус",
        key: "status",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            return statusMap[record.status] ?? null;
          }

          return null;
        },
      },
      {
        title: "Дата подтверждения",
        key: "confirmedAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["confirmed", "paid"].includes(record.status)) return null;

            const formattedDate = record.confirmed_at
              ? new Date(record.confirmed_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
      {
        title: "Дата оплаты",
        key: "paidAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["paid"].includes(record.status)) return null;

            const formattedDate = record.paid_at
              ? new Date(record.paid_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
      {
        title: "Дата закрытия",
        key: "canceledAt",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as MasterListItem;

          if (record.type === "invoice") {
            if (!["canceled"].includes(record.status)) return null;

            const formattedDate = record.canceled_at
              ? new Date(record.canceled_at).toLocaleDateString("ru-RU", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : null;

            return formattedDate;
          }

          return null;
        },
      },
    ];

    return result;
  }, []);

  const tableDataNotPaid = useMemo(() => {
    return list.data.filter(
      (item) =>
        item.type === "invoice" &&
        ["unconfirmed", "confirmed"].includes(item.status)
    );
  }, [list.data]);

  const tableDataAllButNotPaid = useMemo(() => {
    return list.data.filter(
      (item) =>
        item.type === "invoice" &&
        !["unconfirmed", "confirmed"].includes(item.status)
    );
  }, [list.data]);

  return (
    <MobileInnerWrapper>
      <div className={styles.titleBlock}>
        <Title level={3} className={styles.title}>
          Счета
        </Title>
      </div>
      <Title level={5}>Неподтвержденные</Title>
      <Table
        dataSource={tableDataNotPaid}
        columns={columnsNotPaid}
        rowKey="id"
        pagination={false}
        loading={!list.isLoaded}
        styles={styles}
      />
      <div className={styles.additionalTable}>
        <Row
          justify="space-between"
          align="middle"
          className={styles.titleBlock}
        >
          <Col>
            <Title level={5}>Все счета</Title>
          </Col>
          <Col>
            <Button type="primary" onClick={handleShowTable}>
              {isTableHidden ? `Показать` : `Скрыть`}
            </Button>
          </Col>
        </Row>
        <Table
          dataSource={tableDataAllButNotPaid}
          columns={columnsAllButNotPaid}
          rowKey="id"
          pagination={false}
          loading={!list.isLoaded}
          className={clsx(!!isTableHidden && styles.isHidden)}
          styles={styles}
        />
      </div>
    </MobileInnerWrapper>
  );
};

export default Page;
